// Typed client for the server's JSON API (shared/src/api.ts).

import type {
  ActionResponse,
  ApprovalInfo,
  DeviceEventInfo,
  DeviceSummary,
  Enrollment,
  Me,
  ObserveResponse,
  PlannerSettingsUpdate,
  PlannerSettingsView,
  PlannerTestResult,
  ProtoJson,
  ServerInfo,
  TaskConstraintsInput,
  TaskDetail,
  TaskInfo,
} from "@fistos/shared/api";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const write = method !== "GET";
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: write ? { "Content-Type": "application/json" } : {},
    // Every state-changing call carries a JSON body (the server's CSRF rule).
    body: write ? JSON.stringify(body ?? {}) : undefined,
  });
  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, "Sign in required");
  }
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, j.error ?? res.statusText);
  }
  if (res.status === 204 || res.status === 202) return undefined as T;
  return (await res.json()) as T;
}

const enc = encodeURIComponent;

export const api = {
  me: () => request<Me>("GET", "/api/me"),
  server: () => request<ServerInfo>("GET", "/api/server"),
  logout: () => request<void>("POST", "/auth/logout"),

  devices: () => request<DeviceSummary[]>("GET", "/api/devices"),
  device: (id: string) => request<DeviceSummary>("GET", `/api/devices/${enc(id)}`),
  renameDevice: (id: string, name: string) => request<DeviceSummary>("PATCH", `/api/devices/${enc(id)}`, { name }),
  removeDevice: (id: string) => request<void>("DELETE", `/api/devices/${enc(id)}`),
  events: (id: string) => request<DeviceEventInfo[]>("GET", `/api/devices/${enc(id)}/events`),
  enroll: () => request<Enrollment>("POST", "/api/enrollments"),

  action: (id: string, action: ProtoJson) =>
    request<ActionResponse>("POST", `/api/devices/${enc(id)}/action`, { action }),
  observe: (
    id: string,
    req: {
      kind: "ui_tree" | "screenshot";
      subscribe?: boolean;
      unsubscribe?: boolean;
      minIntervalMs?: number;
      quality?: number;
    },
  ) => request<ObserveResponse>("POST", `/api/devices/${enc(id)}/observe`, req),

  tasks: (id: string) => request<TaskInfo[]>("GET", `/api/devices/${enc(id)}/tasks`),
  startTask: (id: string, goal: string, constraints: TaskConstraintsInput) =>
    request<TaskInfo>("POST", `/api/devices/${enc(id)}/tasks`, { goal, constraints }),
  task: (id: string) => request<TaskDetail>("GET", `/api/tasks/${enc(id)}`),
  cancelTask: (id: string) => request<void>("POST", `/api/tasks/${enc(id)}/cancel`),

  approvals: (pendingOnly = false) =>
    request<ApprovalInfo[]>("GET", `/api/approvals${pendingOnly ? "?pending=1" : ""}`),
  decideApproval: (id: string, approved: boolean, note = "") =>
    request<ApprovalInfo>("POST", `/api/approvals/${enc(id)}`, { approved, note }),

  plannerSettings: () => request<PlannerSettingsView>("GET", "/api/settings/planner"),
  savePlannerSettings: (u: PlannerSettingsUpdate) => request<PlannerSettingsView>("PUT", "/api/settings/planner", u),
  testPlanner: () => request<PlannerTestResult>("POST", "/api/settings/planner/test"),
};

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
